import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  useToast,
} from '@kss/ui';
import { ArrowLeft, Save } from 'lucide-react';
import type { PageContent, PageContentCreate } from '@kss/api';
import { pageContentService } from '@kss/api';
import { decodeId } from '../../utils/idCodec';

/**
 * Well-known slugs the storefront looks up by name. Kept in a dropdown so
 * admins don't have to memorise the canonical spelling — a "Custom" escape
 * hatch lets them add ad-hoc slugs (e.g. "gift-cards") without a code
 * change. Backend normalises casing on write; UI just needs to keep it
 * lowercase-friendly.
 */
const WELL_KNOWN_SLUGS: Array<{ value: string; label: string }> = [
  { value: 'our-story', label: 'our-story (About / Our Story)' },
  { value: 'contact-us', label: 'contact-us (Contact page)' },
  { value: 'about', label: 'about' },
  { value: 'shipping', label: 'shipping' },
  { value: 'returns', label: 'returns' },
  { value: 'faq', label: 'faq' },
  { value: 'privacy', label: 'privacy' },
  { value: 'terms', label: 'terms' },
];

type FormState = {
  slug: string;
  slugMode: 'preset' | 'custom';
  customSlug: string;
  title: string;
  body: string;
  metaTitle: string;
  metaDescription: string;
  contactPhone: string;
  contactEmail: string;
  contactAddress: string;
  contactWhatsapp: string;
  isActive: boolean;
};

const emptyForm: FormState = {
  slug: 'our-story',
  slugMode: 'preset',
  customSlug: '',
  title: '',
  body: '',
  metaTitle: '',
  metaDescription: '',
  contactPhone: '',
  contactEmail: '',
  contactAddress: '',
  contactWhatsapp: '',
  isActive: true,
};

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const textareaCls =
  'w-full rounded-md border border-border bg-surface-card px-3 py-2 text-sm text-content-primary outline-none focus:border-primary';

// The storefront hard-codes this slug for its Contact page — the extra
// Phone / Email / WhatsApp / Address fields only make sense here.
const CONTACT_SLUG = 'contact-us';

const stripPhone = (s: string) => s.replace(/\D+/g, '').slice(0, 15);
const normaliseCustomSlug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

export default function NewPageContent() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const isEdit = !!id && id !== 'new';

  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true);
        setLoadError(null);
        try {
          const rec = await pageContentService.get(id);
          if (cancelled) return;
          const slug = (rec.slug ?? '').trim();
          const isPreset = WELL_KNOWN_SLUGS.some((o) => o.value === slug);
          setForm({
            slug: isPreset ? slug : 'our-story',
            slugMode: isPreset ? 'preset' : 'custom',
            customSlug: isPreset ? '' : slug,
            title: rec.title ?? '',
            body: rec.body ?? '',
            metaTitle: rec.metaTitle ?? '',
            metaDescription: rec.metaDescription ?? '',
            contactPhone: rec.contactPhone ?? '',
            contactEmail: rec.contactEmail ?? '',
            contactAddress: rec.contactAddress ?? '',
            contactWhatsapp: rec.contactWhatsapp ?? '',
            isActive: !!rec.isActive,
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setLoadError(msg);
          toastRef.current.error('Load failed', msg);
        } finally {
          if (!cancelled) setLoading(false);
        }
      } else {
        setForm(emptyForm);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const effectiveSlug =
    form.slugMode === 'custom' ? normaliseCustomSlug(form.customSlug) : form.slug;
  const isContact = effectiveSlug === CONTACT_SLUG;

  const validate = (): string | null => {
    if (!effectiveSlug) return 'Slug is required';
    if (!/^[a-z0-9\-]+$/.test(effectiveSlug))
      return 'Slug must be lowercase letters, digits and hyphens only';
    if (!form.title.trim()) return 'Title is required';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      toast.error('Fix errors', err);
      return;
    }
    setSaving(true);
    try {
      if (isEdit && id) {
        const payload: PageContent = {
          pageContentId: id,
          slug: effectiveSlug,
          title: form.title,
          body: form.body || undefined,
          metaTitle: form.metaTitle || undefined,
          metaDescription: form.metaDescription || undefined,
          contactPhone: isContact ? form.contactPhone || undefined : undefined,
          contactEmail: isContact ? form.contactEmail || undefined : undefined,
          contactAddress: isContact ? form.contactAddress || undefined : undefined,
          contactWhatsapp: isContact ? form.contactWhatsapp || undefined : undefined,
          isActive: form.isActive,
        };
        await pageContentService.update([payload]);
        toast.success('Saved', `${form.title} updated`);
      } else {
        const payload: PageContentCreate = {
          slug: effectiveSlug,
          title: form.title,
          body: form.body || undefined,
          metaTitle: form.metaTitle || undefined,
          metaDescription: form.metaDescription || undefined,
          contactPhone: isContact ? form.contactPhone || undefined : undefined,
          contactEmail: isContact ? form.contactEmail || undefined : undefined,
          contactAddress: isContact ? form.contactAddress || undefined : undefined,
          contactWhatsapp: isContact ? form.contactWhatsapp || undefined : undefined,
          isActive: form.isActive,
        };
        await pageContentService.create([payload]);
        toast.success('Saved', `${form.title} added`);
      }
      navigate('/panel/pages');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="-m-6 p-[3px]">
      {loadError && <Alert variant="danger" className="mb-3">{loadError}</Alert>}
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/pages')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit page' : 'New page'}
          </h2>
        </div>

        {loading ? (
          <div className="text-sm text-content-muted">Loading…</div>
        ) : (
          <div className="grid gap-4">
            {/* Slug row */}
            <div className="grid gap-3 md:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">
                  Slug <span className="text-danger">*</span>
                </span>
                <select
                  value={form.slugMode === 'custom' ? '__custom__' : form.slug}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__custom__') {
                      set('slugMode', 'custom');
                    } else {
                      set('slugMode', 'preset');
                      set('slug', v);
                    }
                  }}
                  className={selectCls}
                  disabled={isEdit}
                >
                  {WELL_KNOWN_SLUGS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                  <option value="__custom__">Custom slug…</option>
                </select>
                {isEdit && (
                  <span className="text-xs text-content-muted">
                    Slug is locked while editing to keep public URLs stable.
                  </span>
                )}
              </label>

              {form.slugMode === 'custom' && (
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">
                    Custom slug <span className="text-danger">*</span>
                  </span>
                  <input
                    type="text"
                    value={form.customSlug}
                    onChange={(e) => set('customSlug', e.target.value)}
                    placeholder="e.g. gift-cards"
                    className={inputCls}
                    disabled={isEdit}
                  />
                  <span className="text-xs text-content-muted">
                    Will save as <code>{normaliseCustomSlug(form.customSlug) || '—'}</code>
                  </span>
                </label>
              )}
            </div>

            {/* Title */}
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">
                Title <span className="text-danger">*</span>
              </span>
              <input
                type="text"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Our story"
                className={inputCls}
              />
            </label>

            {/* Body */}
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">
                Body (HTML or Markdown)
              </span>
              <textarea
                value={form.body}
                onChange={(e) => set('body', e.target.value)}
                rows={12}
                placeholder="<p>Our marketplace launched in Bengaluru in 2024…</p>"
                className={`${textareaCls} font-mono text-xs`}
              />
              <span className="text-xs text-content-muted">
                Rendered as-is on the storefront. Use plain HTML or Markdown-like
                syntax — no rich editor required.
              </span>
            </label>

            {/* Meta */}
            <div className="grid gap-3 md:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">
                  Meta title
                </span>
                <input
                  type="text"
                  value={form.metaTitle}
                  onChange={(e) => set('metaTitle', e.target.value)}
                  placeholder="Optional — SEO"
                  className={inputCls}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">
                  Meta description
                </span>
                <input
                  type="text"
                  value={form.metaDescription}
                  onChange={(e) => set('metaDescription', e.target.value)}
                  placeholder="Optional — SEO"
                  className={inputCls}
                />
              </label>
            </div>

            {/* Contact-only fields */}
            {isContact && (
              <div className="grid gap-3 rounded-md border border-border bg-surface-card-hover p-4">
                <h3 className="text-sm font-semibold text-content-primary">
                  Contact card
                </h3>
                <p className="text-xs text-content-muted">
                  These fields only surface on the storefront's Contact page.
                </p>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-secondary">
                      Phone
                    </span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={form.contactPhone}
                      onChange={(e) => set('contactPhone', stripPhone(e.target.value))}
                      placeholder="10–15 digits"
                      className={inputCls}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-secondary">
                      Email
                    </span>
                    <input
                      type="email"
                      value={form.contactEmail}
                      onChange={(e) => set('contactEmail', e.target.value)}
                      placeholder="hello@example.com"
                      className={inputCls}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-secondary">
                      WhatsApp
                    </span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={form.contactWhatsapp}
                      onChange={(e) =>
                        set('contactWhatsapp', stripPhone(e.target.value))
                      }
                      placeholder="10–15 digits, country code included"
                      className={inputCls}
                    />
                  </label>
                  <label className="flex flex-col gap-1 md:col-span-2">
                    <span className="text-xs font-medium text-content-secondary">
                      Address
                    </span>
                    <textarea
                      value={form.contactAddress}
                      onChange={(e) => set('contactAddress', e.target.value)}
                      rows={3}
                      placeholder="Full address, one line per row"
                      className={textareaCls}
                    />
                  </label>
                </div>
              </div>
            )}

            {/* Status */}
            <label className="flex flex-col gap-1 md:w-48">
              <span className="text-xs font-medium text-content-secondary">Status</span>
              <select
                value={form.isActive ? 'active' : 'inactive'}
                onChange={(e) => set('isActive', e.target.value === 'active')}
                className={selectCls}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </label>

            <div className="mt-2 flex items-center justify-end gap-2 border-t border-border pt-4">
              <Button variant="outline" onClick={() => navigate('/panel/pages')}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4" />{' '}
                {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
